import { PAUSAS_POR_JOGO, PAUSA_NA_TELA, competicaoDoJogo, competicoesDosClubes, nomeDaLiga, ehRodadaDeLibertadores, fasesDeMataMata, clubePorId, divisaoDoClube, divisaoDoHumano, ehRodadaDeCopa, nomeDaDivisao, nomeEstadio, nomeCompeticaoDaRodada, textoRodada, type Clube, type ConfrontoCopa, type EstadoJogo, type EventoPartida, type Jogador, type PartidaJogada } from '../core';
import { app, humano, jogoAtual } from './app';
import { intervaloAtivo, restoDaPausa } from './pausa';
import { podeSubstituir } from './solo-ao-vivo';
import { classeReal, tecnicoReal } from './tecnicos-reais';

const segundosIntervalo = (): number => Math.ceil(restoDaPausa() / 1000);
import { escudo, logoCampeonato } from './icones';
import { contraste, cor, esc, num, dinheiroExtenso } from './util';

export function partidaDoUsuario(): PartidaJogada {
  const p = app.partida;
  if (!p) throw new Error('sem partida em andamento');
  return p.rodada.partidas[p.indice] as PartidaJogada;
}

/** Minuto em que a partida está no relógio ao vivo, como "20'", "45+1'" ou "90+3'". */
export function rotuloMinuto(t: number, fim1: number): string {
  const acr1 = fim1 - 45;
  if (t <= 45) return `${t}'`;
  if (t <= fim1) return `45+${t - 45}'`;
  const m = t - acr1;
  return m <= 90 ? `${m}'` : `90+${m - 90}'`;
}

function golsAte(p: PartidaJogada, minuto: number): [number, number] {
  let a = 0;
  let b = 0;
  for (const ev of p.resultado.eventos) {
    if (ev.tipo !== 'gol' || ev.minuto > minuto) continue;
    if (ev.lado === 0) a++;
    else b++;
  }
  return [a, b];
}

function caixaClube(c: Clube, gols: number): string {
  return `<div class="nome-caixa" style="background:${cor(c.cor1)};color:${contraste(c.cor1)}">${esc(c.nome)}</div><span class="gol-num">${gols}</span>`;
}

function iconeEvento(ev: EventoPartida): string {
  if (ev.tipo === 'gol') return '<span class="ev-ico bola" title="Gol">⚽</span>';
  if (ev.tipo === 'amarelo') return '<span class="ev-ico amarelo" title="Cartão amarelo"></span>';
  if (ev.tipo === 'vermelho') return '<span class="ev-ico vermelho" title="Cartão vermelho"></span>';
  if (ev.tipo === 'lesao') return '<span class="ev-ico lesao" title="Lesão">✚</span>';
  if (ev.tipo === 'penalti') return '<span class="ev-ico lesao" title="Pênalti perdido">✖</span>';
  if (ev.tipo === 'tatica') return '<span class="ev-ico sub" title="Mudança de tática">⚙</span>';
  return '<span class="ev-ico sub" title="Substituição">⇄</span>';
}

/** Partida online: aviso de pausa (de qualquer técnico) e o botão para pausar o jogo, com as pausas que restam. */
function botoesOnline(e: EstadoJogo, p: NonNullable<typeof app.partida>): string {
  const rel = app.online?.visao?.sala.aoVivo ?? null;
  const pausa = rel?.pausa ?? null;
  const iv = intervaloAtivo();
  const eu = e.humanoId;
  const usadas = rel?.usadas[eu] ?? 0;
  const restantes = Math.max(0, PAUSAS_POR_JOGO - usadas);
  const segundos = Math.ceil(restoDaPausa() / 1000);
  const faltam = iv ? iv.esperados.filter((id) => !iv.prontos.includes(id)) : [];
  const nomeDe = (id: number): string => app.online?.visao?.sala.jogadores.find((j) => j.clubeId === id)?.nome ?? clubePorId(e, id).nome;
  const aviso = iv
    ? iv.hidratacao
      ? `<div class="pt-aviso pausado"><b>💧 Pausa para hidratação</b><span>${faltam.length ? `Aguardando ${faltam.map(nomeDe).map(esc).join(', ')} (${segundosIntervalo()}s). O jogo volta quando todos confirmarem.` : 'Todos confirmaram: o jogo vai voltar.'}</span></div>`
      : `<div class="pt-aviso pausado"><b>⏸ Intervalo</b><span>${faltam.length ? `Aguardando a escalação de ${faltam.map(nomeDe).map(esc).join(', ')} (${segundosIntervalo()}s). O jogo volta quando todos confirmarem.` : 'Todos confirmaram: o 2º tempo vai começar.'}</span></div>`
    : pausa
    ?`<div class="pt-aviso pausado"><b>⏸ ${pausa.clubeId === eu ? 'Você pausou' : `${esc(pausa.nome)} pausou`} o jogo aos ${pausa.minuto}'</b><span>O servidor espera ${segundos}s e o jogo volta de onde parou.</span></div>`
    : '';
  const emJogo = p.fase === 'ao-vivo' || p.fase === 'intervalo';
  if (p.espectador) return `${aviso}<div class="pt-botoes"><button class="btn principal" data-a="continuar">« Voltar ao time</button></div>`;
  const botao =
    PAUSA_NA_TELA && rel && emJogo
      ?`<button class="btn principal" data-a="pausar-jogo" ${pausa || restantes === 0 ? 'disabled' : ''}>⏸ Pausar para substituir (${restantes} de ${PAUSAS_POR_JOGO})</button>`
      : '';
  return `${aviso}<div class="pt-botoes">${botao}${rel && p.fase === 'fim' ? '<span class="cinza">Aguardando os outros jogos terminarem…</span>' : ''}</div>`;
}

/** Confronto da Copa que esta partida decidiu nos pênaltis (só a partida decisiva da fase), se houve. */
export function disputaDoJogo(e: EstadoJogo, rodada: number, mandante: number, visitante: number): ConfrontoCopa | undefined {
  // online com a rodada ao vivo: o servidor esconde o desfecho das Copas nos outros dados, mas manda a disputa da partida do técnico
  const viva = app.online?.visao?.sala.disputaAoVivo;
  if (viva && ((viva.a === mandante && viva.b === visitante) || (viva.a === visitante && viva.b === mandante))) return viva;
  const fase = fasesDeMataMata(e).find((f) => f.rodadas[f.rodadas.length - 1] === rodada);
  return fase?.confrontos.find((c) => c.cobrancas && ((c.a === mandante && c.b === visitante) || (c.a === visitante && c.b === mandante)));
}

/** Painel da disputa de pênaltis ao vivo: marcadores de cada cobrança e o suspense da próxima. */
function painelPenaltis(e: EstadoJogo, p: NonNullable<typeof app.partida>, c: ConfrontoCopa): string {
  const d = p.disputa;
  const cobrancas = c.cobrancas ?? [];
  if (!d) return '';
  const feitas = cobrancas.slice(0, d.reveladas);
  const nome = (lado: 0 | 1): string => esc(clubePorId(e, lado === 0 ? c.a : c.b).nome);
  const placar: [number, number] = [feitas.filter((x) => x.lado === 0 && x.convertido).length, feitas.filter((x) => x.lado === 1 && x.convertido).length];
  const marcas = (lado: 0 | 1): string =>
    feitas
      .filter((x) => x.lado === lado)
      .map((x) => `<span class="cobranca ${x.convertido ? 'ok' : 'erro'}" title="${esc(x.jogador)}">${x.convertido ? '⚽' : '✖'}</span>`)
      .join('');
  const proxima = cobrancas[d.reveladas];
  const ultima = cobrancas[d.reveladas - 1];
  const fala =
    d.etapa === 'tensao' && proxima
      ? `<div class="pt-aviso tensao"><b>${esc(proxima.jogador)} vai para a bola…</b><span>${nome(proxima.lado)}</span><i class="suspense"><u></u><u></u><u></u></i></div>`
      : ultima
        ? `<div class="pt-aviso ${ultima.convertido ? 'gol' : 'perdeu'}"><b>${ultima.convertido ? 'GOL!' : 'PERDEU!'}</b><span>${esc(ultima.jogador)} ${ultima.convertido ? 'converte a cobrança' : 'desperdiça a cobrança'} (${nome(ultima.lado)})</span></div>`
        : '';
  return `<div class="pt-disputa"><div class="jogo-tit">Disputa de pênaltis</div>
    <div class="disputa-placar"><span>${nome(0)}</span><b>${placar[0]} x ${placar[1]}</b><span>${nome(1)}</span></div>
    <div class="disputa-marcas"><div>${marcas(0)}</div><div>${marcas(1)}</div></div>${fala}</div>`;
}

/** Frase de narração de um lance, como os comentários da partida do original. */
function narracao(e: EstadoJogo, pj: PartidaJogada, ev: EventoPartida): string {
  const nomeTime = clubePorId(e, ev.lado === 0 ? pj.mandante : pj.visitante).nome;
  const j = esc(ev.jogador);
  switch (ev.tipo) {
    case 'gol': {
      const quem = ev.detalhe === 'contra' ? `Gol contra de ${j}!` : ev.detalhe === 'pênalti' ? `Gol! ${j} converte o pênalti!` : ev.detalhe === 'falta' ? `Golaço de falta de ${j}!` : ev.detalhe === 'olímpico' ? `GOL OLÍMPICO de ${j}!` : `Gooool! ${j} balança a rede!`;
      return `${quem} ${esc(nomeTime)}${ev.assistente ? ` (passe de ${esc(ev.assistente)})` : ''}`;
    }
    case 'defesa':
      return `Que defesa de ${j}! ${esc(ev.detalhe ?? '')}`.trim();
    case 'fora':
      return `${j} chuta e a bola passa perto do gol (${esc(nomeTime)}).`;
    case 'penalti':
      return ev.detalhe === 'gol' ? `${j} bate o pênalti e converte.` : ev.detalhe === 'defendido' ? `${j} bate o pênalti e o goleiro defende!` : `${j} bate o pênalti e chuta para fora!`;
    case 'amarelo':
      return `Cartão amarelo para ${j} (${esc(nomeTime)}).`;
    case 'vermelho':
      return `Expulso! ${j} (${esc(nomeTime)}) leva o vermelho${ev.detalhe ? ` — ${esc(ev.detalhe)}` : ''}.`;
    case 'lesao':
      return `${j} (${esc(nomeTime)}) sente lesão — ${esc(ev.detalhe ?? '')}.`;
    case 'substituicao':
      return `Substituição no ${esc(nomeTime)}: entra ${j} ${esc((ev.detalhe ?? '').replace(/^entra /, ''))}.`;
    default:
      return `${esc(nomeTime)}: ${esc(ev.detalhe ?? '')}.`;
  }
}

function comentarios(e: EstadoJogo, pj: PartidaJogada, ate: number, fim1: number): string {
  const linhas = pj.resultado.eventos
    .filter((ev) => ev.minuto <= ate)
    .slice(-14)
    .reverse()
    .map((ev) => `<li class="${ev.tipo}"><b>${rotuloMinuto(ev.minuto, fim1)}</b> ${narracao(e, pj, ev)}</li>`)
    .join('');
  return `<div class="pt-coment"><div class="jogo-tit">Lances</div><ul>${linhas || '<li class="cinza">A bola vai rolar...</li>'}</ul></div>`;
}

const CANTOS = ['◀ Esquerda', '▲ Centro', '▶ Direita'] as const;

/** Canto da cobrança (0 esquerda, 1 centro, 2 direita), o mesmo para todos os técnicos (sai do minuto e do batedor). */
export function direcaoDoPenalti(minuto: number, jogadorId: number): number {
  return (minuto * 31 + jogadorId * 17) % 3;
}

/** Aviso grande do pênalti ao vivo: primeiro a tensão, depois o resultado. */
function avisoPenalti(e: EstadoJogo, p: NonNullable<typeof app.partida>): string {
  const s = p.suspense;
  if (!s) return '';
  const clube = clubePorId(e, s.ev.lado === 0 ? partidaDoUsuario().mandante : partidaDoUsuario().visitante);
  const eu = e.humanoId;
  const sofrendo = app.minigame && clube.id !== eu; // o pênalti é contra o time do técnico
  const batendo = app.minigame && clube.id === eu; // o pênalti é a favor: você escolhe o canto da cobrança
  const chave = `${s.ev.minuto}:${s.ev.jogadorId}`;
  const escolha = p.minigame?.chave === chave ? p.minigame.escolha : null;
  if (s.etapa === 'tensao') {
    const botoes = sofrendo || batendo
      ? `<div class="minigame"><span>${batendo ? 'Mini-game: escolha o canto em que o seu batedor vai chutar!' : 'Mini-game: escolha o canto em que o goleiro vai pular!'}</span>
          <div class="mg-botoes">${CANTOS.map((nome, i) => `<button type="button" class="btn${escolha === i ? ' principal' : ''}" data-a="mg-pular" data-c="${i}" data-k="${chave}" ${escolha !== null ? 'disabled' : ''}>${nome}</button>`).join('')}</div>
          ${escolha !== null ? `<span class="cinza">Você escolheu ${CANTOS[escolha]}. Agora é torcer…</span>` : ''}</div>`
      : '';
    // (o resultado do lance já foi sorteado: o mini-game só decide para onde o chute e o goleiro vão na animação do desfecho)
    return `<div class="pt-aviso tensao"><b>PÊNALTI para ${esc(clube.nome)}!</b><span>${esc(s.ev.jogador)} pega a bola e ajeita para a cobrança...</span><i class="suspense"><u></u><u></u><u></u></i>${botoes}</div>`;
  }
  if (batendo) {
    // o desfecho já foi sorteado; o canto escolhido vira o canto do chute e o goleiro vai coerente com o resultado
    const chute = escolha ?? direcaoDoPenalti(s.ev.minuto, s.ev.jogadorId);
    const outro = (chute + 1 + (s.ev.minuto % 2)) % 3;
    const goleiro = s.ev.detalhe === 'defendido' ? chute : outro;
    const texto =
      s.ev.detalhe === 'gol'
        ? `Você chutou no canto ${CANTOS[chute]} e o goleiro pulou ${CANTOS[goleiro]}: bola na rede!`
        : s.ev.detalhe === 'defendido'
          ? `Você chutou ${CANTOS[chute]}, mas o goleiro adivinhou e pulou no mesmo canto!`
          : `Você chutou ${CANTOS[chute]}, mas a bola saiu para fora.`;
    return `<div class="pt-aviso ${s.ev.detalhe === 'gol' ? 'gol' : 'perdeu'}"><b>${s.ev.detalhe === 'gol' ? 'GOOOL!' : s.ev.detalhe === 'defendido' ? 'DEFENDEU!' : 'PARA FORA!'}</b><span>${esc(s.ev.jogador)} · ${texto}</span></div>`;
  }
  if (sofrendo) {
    // o desfecho do lance já foi sorteado (vale para todos os técnicos); o mini-game mostra se você adivinhou o canto da cobrança
    const canto = direcaoDoPenalti(s.ev.minuto, s.ev.jogadorId);
    const acertou = escolha === canto;
    const dica = escolha === null ? 'Você não escolheu um canto.' : acertou ? `Você pulou no canto certo (${CANTOS[canto]})!` : `Você pulou ${CANTOS[escolha]}, a bola foi ${CANTOS[canto]}.`;
    const titulo = s.ev.detalhe === 'gol' ? 'GOOOL do adversário!' : s.ev.detalhe === 'defendido' ? 'DEFENDEU!' : 'PARA FORA!';
    return `<div class="pt-aviso ${s.ev.detalhe === 'gol' ? 'perdeu' : 'gol'}"><b>${titulo}</b><span>${dica}</span></div>`;
  }
  if (s.ev.detalhe === 'gol') return `<div class="pt-aviso gol"><b>GOOOL!</b><span>${esc(s.ev.jogador)} converte o pênalti para o ${esc(clube.nome)}</span></div>`;
  if (s.ev.detalhe === 'defendido') return `<div class="pt-aviso perdeu"><b>DEFENDEU!</b><span>O goleiro pega a cobrança de ${esc(s.ev.jogador)}</span></div>`;
  return `<div class="pt-aviso perdeu"><b>PARA FORA!</b><span>${esc(s.ev.jogador)} chuta o pênalti por cima do gol</span></div>`;
}

function linhasEventos(p: PartidaJogada, lado: 0 | 1, ate: number, fim1: number): string {
  return p.resultado.eventos
    .filter((ev) => ev.lado === lado && ev.minuto <= ate && ev.tipo !== 'defesa' && ev.tipo !== 'fora' && !(ev.tipo === 'penalti' && ev.detalhe === 'gol'))
    .map((ev) => {
      if (ev.tipo === 'substituicao') return `<li>${iconeEvento(ev)}<span>${esc(ev.jogador)} ${rotuloMinuto(ev.minuto, fim1)} - ${ev.tempo}º <i class="cinza">${esc(ev.detalhe ?? '')}</i></span></li>`;
      if (ev.tipo === 'tatica') return `<li>${iconeEvento(ev)}<span><i class="cinza">${rotuloMinuto(ev.minuto, fim1)} - ${esc(ev.detalhe ?? '')}</i></span></li>`;
      const extra =
        ev.tipo === 'gol' && ev.detalhe === 'pênalti'
          ? ' <i class="cinza">(pênalti)</i>'
          : ev.tipo === 'gol' && ev.assistente
            ? ` <i class="cinza">(A: ${esc(ev.assistente)})</i>`
            : ev.tipo === 'vermelho' && ev.detalhe
              ? ` <i class="cinza">(${esc(ev.detalhe)})</i>`
              : ev.tipo === 'penalti'
                ? ` <i class="cinza">(pênalti ${ev.detalhe === 'defendido' ? 'defendido' : 'para fora'})</i>`
                : '';
      return `<li>${iconeEvento(ev)}<span>${esc(ev.jogador)} ${rotuloMinuto(ev.minuto, fim1)} - ${ev.tempo}º${extra}</span></li>`;
    })
    .join('');
}

function estatisticas(p: PartidaJogada, fracao: number): string {
  const s = p.resultado.estat;
  const esc2 = (par: [number, number]): [number, number] => [Math.round(par[0] * fracao), Math.round(par[1] * fracao)];
  const f = esc2(s.finalizacoes);
  const ng = esc2(s.noGol);
  const fo = esc2(s.fora);
  const d = esc2(s.desarmes);
  const er = esc2(s.passesErrados);
  const linha = (a: string | number, nome: string, b: string | number): string => `<div class="est-linha"><b>${a}</b><span>${nome}</span><b>${b}</b></div>`;
  // fôlego do time (energia média dos jogadores de linha) no ponto do jogo em que a tela está: 0 = início ... 6 = fim
  const fis = p.resultado.fisico;
  const ponto = Math.min(6, Math.max(0, Math.floor(fracao * 6 + 1e-9)));
  const folego = fis ? linha(`${fis[0][ponto] ?? '—'}%`, 'fôlego do time', `${fis[1][ponto] ?? '—'}%`) : '';
  return `${linha(`${s.posse[0]}%`, 'posse de bola', `${s.posse[1]}%`)}${folego}${linha(f[0], 'finalizações', f[1])}${linha(`${ng[0]}/${fo[0]}`, 'no gol/fora', `${ng[1]}/${fo[1]}`)}${linha(d[0], 'desarmes', d[1])}${linha(er[0], 'erros passes', er[1])}`;
}

/** Na volta da Copa, mostra o placar da ida e o agregado parcial (mandante e visitante da volta, nesta ordem). */
function placarDaIda(e: EstadoJogo, rodada: number, pj: PartidaJogada, gm: number, gv: number): string {
  const fase = fasesDeMataMata(e).find((f) => f.rodadas.length > 1 && f.rodadas[1] === rodada);
  const ida = fase && e.resultados.find((r) => r.rodada === fase.rodadas[0] && r.mandante === pj.visitante && r.visitante === pj.mandante);
  if (!ida) return '';
  const idaM = ida.gols[1]; // gols do mandante desta volta no jogo de ida (onde foi visitante)
  const idaV = ida.gols[0];
  return `<div class="pt-ida">Jogo de ida: ${esc(clubePorId(e, ida.mandante).nome)} ${ida.gols[0]} x ${ida.gols[1]} ${esc(clubePorId(e, ida.visitante).nome)} · Agregado: ${idaM + gm} x ${idaV + gv}</div>`;
}

/** Jogo da rodada que está aberto na tela: o do técnico ou o que ele clicou na lista. */
export function indiceVisto(): number {
  const p = app.partida;
  if (!p) return 0;
  const i = p.verIndice;
  return i !== undefined && p.rodada.partidas[i] ? i : p.indice;
}

/**
 * Campeonatos desta rodada com algum técnico de verdade jogando (só no online). Devolve null quando não se aplica (jogo solo)
 * ou quando nenhum técnico joga na rodada: aí a lista mostra todos os jogos.
 */
function competicoesComTecnico(e: EstadoJogo, rodada: number, partidas: { mandante: number; visitante: number }[]): Set<string> | null {
  if (!app.online) return null;
  const set = competicoesDosClubes(e, rodada, partidas, (id) => Boolean(tecnicoReal(clubePorId(e, id))));
  return set.size > 0 ? set : null;
}

/** Lista dos jogos da rodada; clicar em qualquer um abre os detalhes dele (placar, lances, estatísticas), como no original. */
function listaOutrosJogos(e: EstadoJogo, ate: number, indice: number, final: boolean): string {
  const p = app.partida;
  if (!p) return '';
  // com Série A e B no jogo a rodada tem os jogos das duas: um título separa cada divisão (a copa não tem)
  const doCopa = ehRodadaDeCopa(e, p.rodada.rodada) || ehRodadaDeLibertadores(e, p.rodada.rodada);
  let divisaoAnterior = '';
  // online: só aparecem os campeonatos em que algum técnico de verdade joga nesta rodada (deixa a tela bem mais leve com o mundo
  // inteiro). O jogo do técnico e o jogo aberto na tela ficam sempre; sem nenhum técnico jogando, mostra tudo como antes.
  const comTecnico = competicoesComTecnico(e, p.rodada.rodada, p.rodada.partidas);
  return p.rodada.partidas
    .map((x, i) => {
      const m = clubePorId(e, x.mandante);
      const v = clubePorId(e, x.visitante);
      if (comTecnico && i !== indice && i !== p.indice && !comTecnico.has(competicaoDoJogo(e, p.rodada.rodada, x))) return '';
      const [a, b] = final ? x.resultado.gols : golsAte(x, ate);
      const dest = (i === indice ? ' destaque' : '') + (i === p.indice ? ' minha' : '');
      // jogo com o mundo inteiro: cada liga estrangeira tem o seu título; as brasileiras seguem por divisão
      const divisao = doCopa ? '' : m.liga && e.mundo ? m.liga : e.acesso ? String(divisaoDoClube(e, m)) : '';
      const titulo = divisao !== divisaoAnterior && divisao !== '' ? `<div class="jogo-div">${m.liga && e.mundo ? esc(nomeDaLiga(m.liga)) : nomeDaDivisao(Number(divisao) as 1 | 2)}</div>` : '';
      divisaoAnterior = divisao;
      return `${titulo}<div class="jogo-linha clicavel${dest}${classeReal(m, v)}" data-a="ver-jogo" data-i="${i}" role="button" tabindex="0" title="Ver os detalhes deste jogo"><span class="est-nome">${esc(nomeEstadio(m))} | ${num(x.publico)}</span>
        <span class="nome-caixa peq${tecnicoReal(m) ? ' real' : ''}" style="background:${cor(m.cor1)};color:${contraste(m.cor1)}" title="${esc(tecnicoReal(m) ?? '')}">${tecnicoReal(m) ? '👤 ' : ''}${esc(m.nome)}</span><b class="pl">${a}</b><b class="pl">${b}</b>
        <span class="nome-caixa peq${tecnicoReal(v) ? ' real' : ''}" style="background:${cor(v.cor1)};color:${contraste(v.cor1)}" title="${esc(tecnicoReal(v) ?? '')}">${tecnicoReal(v) ? '👤 ' : ''}${esc(v.nome)}</span></div>`;
    })
    .join('');
}

export function telaPartidaAoVivo(): string {
  const e = jogoAtual();
  const p = app.partida;
  if (!p) return '';
  const minha = !p.espectador && indiceVisto() === p.indice;
  const pj = p.rodada.partidas[indiceVisto()] as PartidaJogada;
  const m = clubePorId(e, pj.mandante);
  const v = clubePorId(e, pj.visitante);
  const fim1 = pj.resultado.fimPrimeiroTempo;
  const total = pj.resultado.totalMinutos;
  // jogo de outro confronto: segue o relógio da rodada (no online, o relógio global) sem passar do fim dele
  const minutoRef = minha ? p.minuto : Math.min(total, p.online ? (p.minutoGlobal ?? p.minuto) : p.minuto);
  // durante o suspense do pênalti o resultado do lance ainda não aparece no placar nem na lista
  const ate = minha && p.suspense && p.suspense.etapa === 'tensao' ? p.minuto - 1 : minutoRef;
  const [gm, gv] = golsAte(pj, ate);
  const fracao = total > 0 ? minutoRef / total : 1;
  const confrontoPen = minha && p.fase === 'penaltis' ? disputaDoJogo(e, p.rodada.rodada, pj.mandante, pj.visitante) : undefined;
  const rot = minha
    ? p.fase === 'penaltis' ? 'Pênaltis' : p.fase === 'intervalo' ? 'Intervalo' : `${rotuloMinuto(Math.max(1, p.minuto), fim1)} - ${p.minuto <= fim1 ? '1º' : '2º'} tempo`
    : minutoRef >= total ? 'Encerrado' : minutoRef === fim1 ? 'Intervalo' : `${rotuloMinuto(Math.max(1, minutoRef), fim1)} - ${minutoRef <= fim1 ? '1º' : '2º'} tempo`;
  const pct = minha && p.online && p.progresso !== undefined ? Math.round(Math.min(100, p.progresso * 100) * 10) / 10 : Math.min(100, Math.round(fracao * 100));
  const aviso = minha
    ? ''
    : p.espectador
      ? `<div class="pt-aviso ver-outro"><b>${esc(m.nome)} x ${esc(v.nome)}</b><span>Seu clube não joga nesta rodada: você está só acompanhando. Clique em outro jogo da lista para trocar.</span><button class="btn" data-a="continuar">« Voltar ao time</button></div>`
      : `<div class="pt-aviso ver-outro"><b>Você está vendo ${esc(m.nome)} x ${esc(v.nome)}</b><span>Clique em outro jogo da lista para trocar.</span><button class="btn" data-a="ver-jogo" data-i="${p.indice}">« Voltar ao meu jogo</button></div>`;
  return `<div class="bf-partida">
    <div class="pt-topo"><span class="pt-comp">${logoCampeonato(ehRodadaDeLibertadores(e, p.rodada.rodada) ? (nomeCompeticaoDaRodada(e, p.rodada.rodada).includes('Sul') ? 'sulamericana' : 'libertadores') : ehRodadaDeCopa(e, p.rodada.rodada) ? 'copa' : divisaoDoHumano(e) === 2 ? 'serie-b' : 'liga', 26)}${esc(nomeCompeticaoDaRodada(e, p.rodada.rodada))}</span><div class="pt-prog" role="progressbar" aria-valuenow="${pct}" aria-valuemin="0" aria-valuemax="100"><i style="width:${pct}%"></i></div><span>${rot}</span></div>
    <div class="pt-placar">
      <div class="pt-est"><span>${esc(nomeEstadio(m))}</span><b>${num(pj.publico)}</b></div>
      ${escudo(m, true)}
      <div class="pt-times"><div class="pt-rod">${textoRodada(e, p.rodada.rodada)}</div>
        <div class="pt-linha">${caixaClube(m, gm).replace('<span class="gol-num">' + gm + '</span>', '')}<b class="pt-gols">${gm} x ${gv}</b>${caixaClube(v, gv).replace('<span class="gol-num">' + gv + '</span>', '')}</div>
        ${placarDaIda(e, p.rodada.rodada, pj, gm, gv)}</div>
      ${escudo(v, true)}
      <div class="pt-stat">${estatisticas(pj, fracao)}</div>
    </div>
    ${aviso}
    ${minha ? (confrontoPen ? painelPenaltis(e, p, confrontoPen) : avisoPenalti(e, p)) : ''}
    ${comentarios(e, pj, ate, fim1)}
    <div class="pt-eventos"><ul>${linhasEventos(pj, 0, ate, fim1)}</ul><ul>${linhasEventos(pj, 1, ate, fim1)}</ul></div>
    <div class="pt-jogos"><div class="jogo-tit">${textoRodada(e, p.rodada.rodada)}</div>${listaOutrosJogos(e, p.online ? (p.minutoGlobal ?? ate) : ate, indiceVisto(), false)}</div>
    ${p.online ? botoesOnline(e, p) : `${p.pausado ? '<div class="pt-aviso pausado"><b>⏸ Jogo pausado</b><span>Clique em Continuar para voltar de onde parou.</span></div>' : ''}<div class="pt-botoes">${minha && p.fase !== 'fim' ? `<button class="btn principal" data-a="pausa-local">${p.pausado ? '▶ Continuar' : '⏸ Pausar'}</button>` : ''}${minha && p.pausado && podeSubstituir() ? '<button class="btn" data-a="pausa-substituir">🔁 Substituições e tática</button>' : ''}<button class="btn" data-a="pular-partida">Pular para o final</button>
      <button class="btn" data-a="ritmo-partida">Velocidade: ${app.velocidade === 3 ? 'rápida' : 'normal'}</button></div>`}
  </div>`;
}

function nomeDe(c: Clube, id: number): Jogador | undefined {
  return c.elenco.find((j) => j.id === id);
}

export function telaFimDeJogo(): string {
  const e = jogoAtual();
  const p = app.partida;
  if (!p) return '';
  const pj = p.rodada.partidas[indiceVisto()] as PartidaJogada;
  const m = clubePorId(e, pj.mandante);
  const v = clubePorId(e, pj.visitante);
  const r = pj.resultado;
  const fim1 = r.fimPrimeiroTempo;
  const outro = !p.espectador && indiceVisto() !== p.indice;

  const lista = (c: Clube, lado: 0 | 1): string => {
    const ps = r.participacoes.filter((x) => x.lado === lado);
    const melhor = Math.max(...ps.map((x) => x.nota));
    const linhas = ps
      .sort((a, b) => Number(b.titular) - Number(a.titular))
      .map((x) => {
        const j = nomeDe(c, x.jogadorId);
        const nota = x.nota.toFixed(1).replace('.', ',');
        const marcas = `${x.gols ? '⚽'.repeat(x.gols) : ''}${x.vermelho ? '<span class="ev-ico vermelho"></span>' : x.amarelos ? '<span class="ev-ico amarelo"></span>' : ''}${x.assistencias ? '<span class="ev-ico ass" title="Assistência">A</span>' : ''}`;
        return `<li class="${x.titular ? '' : 'reserva'}"><span>${j ? j.posicao : '?'} - ${esc(j?.nome ?? '—')} ${marcas}</span><b class="nota ${x.nota === melhor ? 'top' : ''}">${nota}</b></li>`;
      })
      .join('');
    return `<div class="fj-time"><div class="fj-cab" style="background:${cor(c.cor1)};color:${contraste(c.cor1)}">${esc(c.nome)}</div><ul class="fj-jog">${linhas}</ul></div>`;
  };

  const gols = r.eventos
    .filter((ev) => ev.tipo === 'gol')
    .map((ev) => `<li>${iconeEvento(ev)} ${esc(ev.jogador)} ${rotuloMinuto(ev.minuto, fim1)} - ${ev.tempo}º ${ev.assistente ? `<i class="cinza">(A: ${esc(ev.assistente)})</i>` : ''}</li>`)
    .join('');
  const cartoes = r.eventos
    .filter((ev) => ev.tipo === 'amarelo' || ev.tipo === 'vermelho')
    .map((ev) => `<li>${iconeEvento(ev)} ${esc(ev.jogador)} ${rotuloMinuto(ev.minuto, fim1)} - ${ev.tempo}º</li>`)
    .join('');
  const subs = (lado: 0 | 1): string =>
    r.eventos
      .filter((ev) => ev.tipo === 'substituicao' && ev.lado === lado)
      .map((ev) => `<li>${iconeEvento(ev)} ${esc(ev.jogador)} ${rotuloMinuto(ev.minuto, fim1)} - ${ev.tempo}º <i class="cinza">${esc(ev.detalhe ?? '')}</i></li>`)
      .join('');
  const lesoes = r.eventos
    .filter((ev) => ev.tipo === 'lesao')
    .map((ev) => `<li>${iconeEvento(ev)} ${esc(ev.jogador)} ${rotuloMinuto(ev.minuto, fim1)} <i class="cinza">${esc(ev.detalhe ?? '')}</i></li>`)
    .join('');

  return `<div class="bf-fimjogo">
    <div class="fj-esq">
      <div class="fj-rod"><div class="jogo-tit">${textoRodada(e, p.rodada.rodada)}</div>${listaOutrosJogos(e, p.minutoGlobal ?? 999, indiceVisto(), !(p.online && app.online?.visao?.sala.aoVivo))}</div></div>
    <div class="fj-centro">
      ${outro ? `<div class="pt-aviso ver-outro"><b>Você está vendo ${esc(m.nome)} x ${esc(v.nome)}</b><button class="btn" data-a="ver-jogo" data-i="${p.indice}">« Voltar ao meu jogo</button></div>` : ''}
      <div class="fj-placar">${escudo(m, true)}<b>${r.gols[0]} x ${r.gols[1]}</b>${escudo(v, true)}</div>
      ${(() => {
        const d = disputaDoJogo(e, p.rodada.rodada, pj.mandante, pj.visitante);
        if (!d?.penaltis) return '';
        const [pm, pv] = d.a === pj.mandante ? d.penaltis : [d.penaltis[1], d.penaltis[0]];
        return `<div class="fj-pen">Pênaltis: ${esc(m.nome)} ${pm} x ${pv} ${esc(v.nome)}</div>`;
      })()}
      <div class="fj-dir">
        ${lista(m, 0)}
        <div class="fj-meio">
          <div class="fj-est"><span>${esc(nomeEstadio(m))}</span><b>${num(pj.publico)}</b><i>${dinheiroExtenso(pj.renda)}</i></div>
          <div class="fj-bloco"><div class="fj-tit">Gols</div><ul>${gols || '<li class="cinza">Sem gols</li>'}</ul></div>
          <div class="fj-bloco"><div class="fj-tit">Cartões</div><ul>${cartoes || '<li class="cinza">Sem cartões</li>'}</ul></div>
          ${lesoes ? `<div class="fj-bloco"><div class="fj-tit">Lesões</div><ul>${lesoes}</ul></div>` : ''}
          <div class="fj-stat">${estatisticas(pj, 1)}</div>
        </div>
        ${lista(v, 1)}
      </div>
      <div class="fj-subs"><div><div class="fj-tit">Substituições</div><ul>${subs(0) || '<li class="cinza">—</li>'}</ul></div>
        <div><div class="fj-tit">Substituições</div><ul>${subs(1) || '<li class="cinza">—</li>'}</ul></div></div>
      <div class="pt-botoes"><button class="btn principal" data-a="continuar">Continuar »</button></div>
    </div></div>`;
}

export function telaPartida(): string {
  const p = app.partida;
  if (!p) return '';
  // o resumo final de outro jogo só vale quando a rodada inteira terminou (no online ela pode ainda estar rolando)
  const rodadaAcabou = !(p.online && app.online?.visao?.sala.aoVivo);
  const vendoOutro = p.espectador || indiceVisto() !== p.indice;
  return p.fase === 'fim' && (!vendoOutro || rodadaAcabou) ? telaFimDeJogo() : telaPartidaAoVivo();
}

/** Para a tela principal saber o nome do humano na partida (mantido para uso futuro). */
export function clubeHumanoNaPartida(): Clube {
  return humano();
}
